import { useQueryClient } from 'react-query';

import {
  getPayableQueryKey,
  getToPreparePayableListQueryKey,
} from 'modules/payable/hooks';
import { type PayableId } from 'modules/payable/models';
import { getFetcher } from 'src/core/api/fetcher';
import { type GraphQlQueryRequest } from 'src/core/api/query';

import { type RawPayable } from './graphql-reshaper';
import { GET_PAYABLE } from './queries';
import { payableVersionWhenPartiallyLoaded } from '../../../prepare-payables/models';
import { type PayableFromRestApi } from '../../useGetPayable';

/**
 * Query and cache config
 */

export type PayableGraphQLResponse = {
  payable: RawPayable;
};

export type PayableGraphQlVariables = {
  payableId: PayableId;
};

const getGraphQlQueryConfig = (
  payableId: PayableId | null,
): GraphQlQueryRequest => {
  const variables: PayableGraphQlVariables = { payableId: payableId ?? '' };
  return {
    type: 'graphQL',
    target: 'v2',
    query: GET_PAYABLE,
    variables,
  };
};

export const useInvalidateGetPayableQueryCache = () => {
  const queryClient = useQueryClient();

  return async (
    companyId: string,
    payableId: PayableId | null,
  ): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries(getPayableQueryKey(companyId, payableId)),
      // Cached payable details in Prepare
      queryClient.invalidateQueries(getToPreparePayableListQueryKey(companyId)),
    ]);
  };
};

export const useUpdateGetPayableQueryCache = () => {
  const queryClient = useQueryClient();

  return (
    companyId: string,
    payableId: PayableId | null,
    payableUpdate: Partial<RawPayable>,
  ) => {
    const payable: PayableFromRestApi | RawPayable | undefined =
      queryClient.getQueryData<PayableGraphQLResponse>(
        getPayableQueryKey(companyId, payableId),
      )?.payable;

    // Skip "partially loaded" update if we have a "fully loaded" cached payable
    if (
      payableUpdate?.version === payableVersionWhenPartiallyLoaded &&
      (payable?.version ?? -1) > payableVersionWhenPartiallyLoaded
    ) {
      return;
    }

    // Skip other update if we don't have a cached payable or if the update has not a higher version
    if (
      (payable?.version ?? Number.POSITIVE_INFINITY) >
      (payableUpdate?.version ?? 0)
    ) {
      return;
    }

    if (payableId) {
      queryClient.setQueryData<PayableGraphQLResponse>(
        getPayableQueryKey(companyId, payableId),
        {
          payable: {
            id: payableId,
            version: payableVersionWhenPartiallyLoaded,
            ...payable,
            ...payableUpdate,
          } as RawPayable,
        },
      );
    }

    // TODO@financeAccountant@CORE-4887 update cache for REST
  };
};

/**
 * Standalone GraphQL fetcher
 */

export const getPayableQueryFetcher = ({
  companyId,
  payableId,
}: {
  companyId: string;
  payableId: string;
}): (() => Promise<PayableGraphQLResponse>) =>
  getFetcher<PayableGraphQLResponse>({
    companyId,
    getRequest: () => getGraphQlQueryConfig(payableId),
  });
